import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import useSettings from "./hooks/useSettings";

const defaultLogoLight = "/logo-light.svg";
const defaultLogoDark = "/logo-dark.svg";
const defaultLogoFavicon = "/favicon.png";
const BRAND_PRIMARY = "#163149";
const BRAND_PRIMARY_DARK = "#31506B";
const BRAND_BACKGROUND_DARK = "#101922";
const BRAND_PAPER_DARK = "#16232F";
const BRAND_TEXT_DARK = "#F4F7FA";
const BRAND_TEXT_DARK_SECONDARY = "#B8C6D3";
const BRAND_TEXT_LIGHT = "#163149";
const BRAND_TEXT_LIGHT_SECONDARY = "#4E6478";

const queryClient = new QueryClient();

const defaultPublicAssets = new Set([
  "logo-light.svg",
  "logo-dark.svg",
  "favicon.png",
  "favicon.ico",
  "favicon-16x16.png",
  "favicon-32x32.png",
]);

const resolvePublicAssetUrl = (value, fallback) => {
  if (!value) return fallback;
  if (value.startsWith("http") || value.startsWith("/")) return value;
  if (defaultPublicAssets.has(value)) return `/${value}`;
  return `${getBackendUrl()}/public/${value}`;
};

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage =
    localStorage.getItem("primaryColorLight") ||
    localStorage.getItem("primaryColorDark") ||
    BRAND_PRIMARY;
  const appNameLocalStorage = localStorage.getItem("appName") || "SOLUWHATS";
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();
  // Estado para controlar o prompt de instalação do PWA
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        document.body.classList.add("theme-transitioning");
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode);
          return newMode;
        });
        setTimeout(() => {
          document.body.classList.remove("theme-transitioning");
        }, 400);
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          overrides: {
            MuiCssBaseline: {
              "@global": {
                body: {
                  backgroundColor: mode === "light" ? "#F4F7FA" : BRAND_BACKGROUND_DARK,
                  color: mode === "light" ? BRAND_TEXT_LIGHT : BRAND_TEXT_DARK,
                },
                ".MuiTypography-root, .MuiInputBase-input, .MuiInputLabel-root, .MuiFormLabel-root, .MuiMenuItem-root, .MuiListItemText-primary": {
                  color: mode === "light" ? BRAND_TEXT_LIGHT : BRAND_TEXT_DARK,
                },
                ".MuiListItemText-secondary, .MuiFormHelperText-root, .MuiTypography-colorTextSecondary": {
                  color:
                    mode === "light"
                      ? BRAND_TEXT_LIGHT_SECONDARY
                      : BRAND_TEXT_DARK_SECONDARY,
                },
              },
            },
            MuiPaper: {
              root: {
                backgroundColor: mode === "light" ? "#FFFFFF" : BRAND_PAPER_DARK,
                color: mode === "light" ? BRAND_TEXT_LIGHT : BRAND_TEXT_DARK,
              },
            },
            MuiDialogTitle: {
              root: {
                backgroundColor: mode === "light" ? "#FFFFFF" : "#1A2734",
                color: mode === "light" ? BRAND_TEXT_LIGHT : BRAND_TEXT_DARK,
                borderBottom:
                  mode === "light"
                    ? "1px solid rgba(22, 49, 73, 0.10)"
                    : "1px solid rgba(244, 247, 250, 0.10)",
              },
            },
            MuiDialogContent: {
              dividers: {
                borderTop:
                  mode === "light"
                    ? "1px solid rgba(22, 49, 73, 0.10)"
                    : "1px solid rgba(244, 247, 250, 0.10)",
                borderBottom:
                  mode === "light"
                    ? "1px solid rgba(22, 49, 73, 0.10)"
                    : "1px solid rgba(244, 247, 250, 0.10)",
              },
            },
            MuiDialogActions: {
              root: {
                backgroundColor: mode === "light" ? "#FFFFFF" : "#1A2734",
                borderTop:
                  mode === "light"
                    ? "1px solid rgba(22, 49, 73, 0.10)"
                    : "1px solid rgba(244, 247, 250, 0.10)",
                padding: "12px 16px",
              },
            },
            MuiOutlinedInput: {
              root: {
                backgroundColor: mode === "light" ? "#FFFFFF" : BRAND_PAPER_DARK,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    mode === "light"
                      ? "rgba(22, 49, 73, 0.36)"
                      : "rgba(244, 247, 250, 0.34)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: mode === "light" ? primaryColorLight : "#7FA8CC",
                },
              },
              input: {
                color: mode === "light" ? BRAND_TEXT_LIGHT : BRAND_TEXT_DARK,
              },
              notchedOutline: {
                borderColor:
                  mode === "light"
                    ? "rgba(22, 49, 73, 0.18)"
                    : "rgba(244, 247, 250, 0.18)",
              },
            },
            MuiInputLabel: {
              root: {
                color:
                  mode === "light"
                    ? BRAND_TEXT_LIGHT_SECONDARY
                    : BRAND_TEXT_DARK_SECONDARY,
              },
              outlined: {
                "&.Mui-focused": {
                  color: mode === "light" ? primaryColorLight : "#D7E5F2",
                },
              },
            },
            MuiFormLabel: {
              root: {
                color:
                  mode === "light"
                    ? BRAND_TEXT_LIGHT_SECONDARY
                    : BRAND_TEXT_DARK_SECONDARY,
              },
            },
            MuiSelect: {
              icon: {
                color: mode === "light" ? BRAND_TEXT_LIGHT_SECONDARY : BRAND_TEXT_DARK,
              },
            },
            MuiButton: {
              root: {
                borderRadius: 8,
                textTransform: "none",
                fontWeight: 600,
                "&$disabled": {
                  color:
                    mode === "light"
                      ? "rgba(22, 49, 73, 0.35) !important"
                      : "rgba(244, 247, 250, 0.45) !important",
                  borderColor:
                    mode === "light"
                      ? "rgba(22, 49, 73, 0.12) !important"
                      : "rgba(244, 247, 250, 0.14) !important",
                  backgroundColor:
                    mode === "light"
                      ? "rgba(22, 49, 73, 0.06) !important"
                      : "rgba(255, 255, 255, 0.06) !important",
                },
              },
              containedPrimary: {
                backgroundColor: mode === "light" ? primaryColorLight : "#4F78A0",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: mode === "light" ? "#214B6E" : "#5C89B5",
                },
              },
              outlined: {
                borderColor:
                  mode === "light"
                    ? "rgba(22, 49, 73, 0.24)"
                    : "rgba(244, 247, 250, 0.28)",
                color: mode === "light" ? BRAND_TEXT_LIGHT : BRAND_TEXT_DARK,
              },
              outlinedPrimary: {
                borderColor:
                  mode === "light"
                    ? "rgba(22, 49, 73, 0.34)"
                    : "rgba(127, 168, 204, 0.7)",
                color: mode === "light" ? primaryColorLight : "#D7E5F2",
              },
              outlinedSecondary: {
                borderColor:
                  mode === "light"
                    ? "rgba(22, 49, 73, 0.24)"
                    : "rgba(244, 247, 250, 0.28)",
                color: mode === "light" ? BRAND_TEXT_LIGHT : "#DDEAF6",
              },
              textSecondary: {
                color: mode === "light" ? BRAND_TEXT_LIGHT : "#DDEAF6",
              },
              label: {
                color: "inherit",
              },
              disabled: {},
            },
          },
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
            },
          },
          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#F3F3F3" : "#333333",
            },
          },
          palette: {
            type: mode,
            primary: {
              main: mode === "light" ? primaryColorLight : primaryColorDark,
              contrastText: "#FFFFFF",
            },
            secondary: {
              main: mode === "light" ? primaryColorDark : primaryColorLight,
              contrastText: "#FFFFFF",
            },
            text: {
              primary: mode === "light" ? BRAND_TEXT_LIGHT : BRAND_TEXT_DARK,
              secondary:
                mode === "light"
                  ? BRAND_TEXT_LIGHT_SECONDARY
                  : BRAND_TEXT_DARK_SECONDARY,
            },
            background: {
              default: mode === "light" ? "#F4F7FA" : BRAND_BACKGROUND_DARK,
              paper: mode === "light" ? "#FFFFFF" : BRAND_PAPER_DARK,
            },
            divider:
              mode === "light" ? "rgba(22, 49, 73, 0.14)" : "rgba(244, 247, 250, 0.16)",
            textPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
            light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
            fontColor: mode === "light" ? BRAND_TEXT_LIGHT : BRAND_TEXT_DARK,
            tabHeaderBackground: mode === "light" ? "#EEF3F7" : BRAND_PAPER_DARK,
            optionsBackground: mode === "light" ? "#FAFCFE" : BRAND_PAPER_DARK,
            fancyBackground: mode === "light" ? "#FAFCFE" : BRAND_PAPER_DARK,
            total: mode === "light" ? "#FFFFFF" : BRAND_PAPER_DARK,
            messageIcons: mode === "light" ? "#6E7F90" : BRAND_TEXT_DARK_SECONDARY,
            inputBackground: mode === "light" ? "#FFFFFF" : BRAND_PAPER_DARK,
            barraSuperior: mode === "light" ? primaryColorLight : BRAND_PRIMARY,
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, primaryColorDark, primaryColorLight]
  );

  // Detecta quando o navegador está pronto para mostrar o prompt de instalação do PWA
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Previne o comportamento padrão do navegador
      e.preventDefault();
      // Armazena o evento para uso posterior
      setDeferredPrompt(e);
      
      // Mostra o prompt de instalação imediatamente
      setTimeout(() => {
        showInstallPrompt();
      }, 2000); // Pequeno delay para garantir que a página já carregou
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Função para mostrar o prompt de instalação
  const showInstallPrompt = () => {
    if (deferredPrompt) {
      // Verifica se o PWA já está instalado
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        // Mostra o prompt de instalação
        deferredPrompt.prompt();
        
        // Espera pela resposta do usuário
        deferredPrompt.userChoice.then((choiceResult) => {
          if (choiceResult.outcome === 'accepted') {
          } else {
          }
          // Limpa o prompt armazenado, só pode ser usado uma vez
          setDeferredPrompt(null);
        });
      }
    }
  };

  useEffect(() => {
    try {
      const i18nlocale = localStorage.getItem("i18nextLng") || "es";
      const browserLocale = i18nlocale.substring(0, 2) + (i18nlocale.length > 3 ? i18nlocale.substring(3, 5) : "");

      if (browserLocale === "ptBR" || browserLocale === "pt") {
        setLocale(ptBR);
      }
    } catch (e) {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  useEffect(() => {
    // WhiteLabel: Carregar TODAS as configurações com cache-first + paralelização
    const loadWhiteLabelSettings = async () => {
      const settingsConfig = [
        { key: 'appName', setter: setAppName, defaultValue: 'SOLUWHATS', cache: true },
        { key: 'primaryColorLight', setter: setPrimaryColorLight, defaultValue: BRAND_PRIMARY, cache: true },
        { key: 'primaryColorDark', setter: setPrimaryColorDark, defaultValue: BRAND_PRIMARY_DARK, cache: true },
        { key: 'appLogoLight', setter: setAppLogoLight, defaultValue: defaultLogoLight, cache: true, isFile: true },
        { key: 'appLogoDark', setter: setAppLogoDark, defaultValue: defaultLogoDark, cache: true, isFile: true },
        { key: 'appLogoFavicon', setter: setAppLogoFavicon, defaultValue: defaultLogoFavicon, cache: true, isFile: true }
      ];

      // ETAPA 1: Carregar cache PRIMEIRO (síncrono, instantâneo)
      settingsConfig.forEach(({ key, setter, defaultValue, cache, isFile }) => {
        if (cache) {
          const cachedValue = localStorage.getItem(key);
          if (cachedValue && cachedValue !== 'null' && cachedValue !== 'undefined') {
            const value = isFile
              ? resolvePublicAssetUrl(cachedValue, defaultValue)
              : cachedValue;

            setter(value);

            // Atualiza document.title imediatamente se for appName
            if (key === 'appName') {
              document.title = cachedValue;
            }

          }
        }
      });

      // ETAPA 2: Executar TODAS as chamadas API EM PARALELO

      const promises = settingsConfig.map(({ key }) =>
        getPublicSetting(key).catch(error => {
          return null;
        })
      );

      const results = await Promise.allSettled(promises);

      // ✅ ETAPA 3: Processar resultados e atualizar estados + cache
      results.forEach((result, index) => {
        const { key, setter, defaultValue, cache, isFile } = settingsConfig[index];

        if (result.status === 'fulfilled' && result.value !== null) {
          let value = result.value || defaultValue;

          // Processar arquivos (adicionar URL base se necessário)
          if (isFile) {
            value = resolvePublicAssetUrl(value, defaultValue);
          }

          setter(value);

          // Salvar no cache
          if (cache) {
            localStorage.setItem(key, result.value || defaultValue);
          }

          // Atualizar document.title se for appName
          if (key === 'appName') {
            document.title = result.value || defaultValue;
          }

        } else {
          // Se falhou, usar valor padrão (se não tiver cache)
          const cachedValue = localStorage.getItem(key);
          if (!cachedValue) {
            let value = defaultValue;
            if (isFile) {
              value = resolvePublicAssetUrl(value, defaultValue);
            }
            setter(value);
          }
        }
      });

    };

    loadWhiteLabelSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ WhiteLabel: useEffect reativo para atualizar document.title quando appName muda
  useEffect(() => {
    if (appName && appName !== 'null' && appName !== 'undefined') {
      document.title = appName;
    }
  }, [appName]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primaryColor", mode === "light" ? primaryColorLight : primaryColorDark);
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={resolvePublicAssetUrl(appLogoFavicon, defaultLogoFavicon)} />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
  <div style={{ position: "relative", overflowX: "hidden", overflowY: "auto", zIndex: 0, minHeight: "100vh" }}>
    <Routes />
  </div>
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
